import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  setDoc,
  deleteDoc,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { FloorSpace } from '../../types'
import { dispatchCatalogEvent, FLOOR_UPDATED_EVENT } from '../catalogEvents'
import { getFirestoreInstance } from '../firebase'
import { getCurrentPlaceId } from '../placeConfig'
import { sortFloorSpacesByIndex } from '../menuSortOrder'
import { stripUndefined } from './stripUndefined'
import { commitWriteOps, type BatchWriteOp } from './writeBatches'

const floorSpacesCacheByPlace: Record<string, FloorSpace[]> = {}
let floorSpacesUnsubscribe: (() => void) | null = null

function dispatchFloorUpdated(): void {
  dispatchCatalogEvent(FLOOR_UPDATED_EVENT)
}

function floorSpacesRef(placeId: string) {
  return collection(getFirestoreInstance(), 'places', placeId, 'floorSpaces')
}

function floorSpaceDoc(placeId: string, spaceId: string) {
  return doc(getFirestoreInstance(), 'places', placeId, 'floorSpaces', spaceId)
}

function docToFloorSpace(d: QueryDocumentSnapshot): FloorSpace {
  const data = d.data()
  const sortIndex =
    typeof data.sortIndex === 'number' && Number.isFinite(data.sortIndex)
      ? data.sortIndex
      : undefined
  const color =
    typeof data.color === 'string' && data.color.trim() ? data.color.trim() : '#6366f1'
  const sourceAreaId =
    typeof data.sourceAreaId === 'string' && data.sourceAreaId.trim()
      ? data.sourceAreaId.trim()
      : undefined
  return {
    id: d.id,
    name: (data.name as string) ?? '',
    color,
    sortIndex,
    sourceAreaId,
  }
}

export async function primeFloorSpacesCache(): Promise<void> {
  const placeId = getCurrentPlaceId()
  if (floorSpacesUnsubscribe) {
    floorSpacesUnsubscribe()
    floorSpacesUnsubscribe = null
  }
  const snap = await getDocs(floorSpacesRef(placeId))
  floorSpacesCacheByPlace[placeId] = sortFloorSpacesByIndex(snap.docs.map(docToFloorSpace))
  floorSpacesUnsubscribe = onSnapshot(floorSpacesRef(placeId), (snapshot) => {
    floorSpacesCacheByPlace[placeId] = sortFloorSpacesByIndex(
      snapshot.docs.map(docToFloorSpace),
    )
    dispatchFloorUpdated()
  })
}

export function getFloorSpaces(): FloorSpace[] {
  const placeId = getCurrentPlaceId()
  return floorSpacesCacheByPlace[placeId] ?? []
}

export function saveFloorSpaces(spaces: FloorSpace[]): void {
  const placeId = getCurrentPlaceId()
  const withIndex = spaces.map((s, i) => ({ ...s, sortIndex: i }))
  floorSpacesCacheByPlace[placeId] = withIndex
  dispatchFloorUpdated()
  ;(async () => {
    const snap = await getDocs(floorSpacesRef(placeId))
    const ids = new Set(withIndex.map((s) => s.id))
    const ops: BatchWriteOp[] = []
    for (const d of snap.docs) {
      if (!ids.has(d.id)) ops.push({ type: 'delete', ref: d.ref })
    }
    for (const s of withIndex) {
      ops.push({
        type: 'set',
        ref: floorSpaceDoc(placeId, s.id),
        data: stripUndefined(s) as Record<string, unknown>,
      })
    }
    await commitWriteOps(ops)
  })().catch((e) => console.error('Firestore saveFloorSpaces failed', e))
}

function generateFloorSpaceId(): string {
  return `FS-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function addFloorSpace(space: Omit<FloorSpace, 'id'>): FloorSpace {
  const placeId = getCurrentPlaceId()
  const id = generateFloorSpaceId()
  const cache = floorSpacesCacheByPlace[placeId] ?? []
  const maxIdx = cache.reduce((m, s) => Math.max(m, s.sortIndex ?? -1), -1)
  const full: FloorSpace = {
    ...space,
    id,
    color: space.color?.trim() || '#6366f1',
    sortIndex: maxIdx + 1,
  }
  floorSpacesCacheByPlace[placeId] = [...cache, full]
  dispatchFloorUpdated()
  setDoc(floorSpaceDoc(placeId, id), stripUndefined(full)).catch((e) =>
    console.error('Firestore addFloorSpace failed', e),
  )
  return full
}

export function updateFloorSpace(id: string, patch: Partial<FloorSpace>): FloorSpace[] {
  const placeId = getCurrentPlaceId()
  const cache = floorSpacesCacheByPlace[placeId] ?? []
  floorSpacesCacheByPlace[placeId] = cache.map((s) =>
    s.id === id ? { ...s, ...patch, color: patch.color?.trim() || s.color } : s,
  )
  const found = floorSpacesCacheByPlace[placeId].find((s) => s.id === id)
  if (found) {
    setDoc(floorSpaceDoc(placeId, id), stripUndefined(found)).catch((e) =>
      console.error('Firestore updateFloorSpace failed', e),
    )
  }
  dispatchFloorUpdated()
  return floorSpacesCacheByPlace[placeId]
}

export function deleteFloorSpace(id: string): FloorSpace[] {
  const placeId = getCurrentPlaceId()
  const cache = floorSpacesCacheByPlace[placeId] ?? []
  floorSpacesCacheByPlace[placeId] = cache.filter((s) => s.id !== id)
  deleteDoc(floorSpaceDoc(placeId, id)).catch((e) =>
    console.error('Firestore deleteFloorSpace failed', e),
  )
  dispatchFloorUpdated()
  return floorSpacesCacheByPlace[placeId]
}

export function reorderFloorSpaces(orderedIds: string[]): void {
  const placeId = getCurrentPlaceId()
  const cache = floorSpacesCacheByPlace[placeId] ?? []
  const byId = new Map(cache.map((s) => [s.id, s]))
  const newOrder = orderedIds
    .map((id) => byId.get(id))
    .filter((s): s is FloorSpace => s != null)
  saveFloorSpaces(newOrder)
}

export function isFloorSpacesPrimed(): boolean {
  const placeId = getCurrentPlaceId()
  return placeId in floorSpacesCacheByPlace
}

export function clearFloorSpacesCache(): void {
  if (floorSpacesUnsubscribe) {
    floorSpacesUnsubscribe()
    floorSpacesUnsubscribe = null
  }
  Object.keys(floorSpacesCacheByPlace).forEach((k) => delete floorSpacesCacheByPlace[k])
}
